import languagesJson from 'shared/languages'
import { z } from 'zod/mini'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { config } from '~/config/env'

enum CardsFront {
  learn = 'learn',
  source = 'source',
}

export type CardsFrontUnion = `${CardsFront}`

export type Language = { label: string; value: string }

export type TourProgress = {
  completed: boolean
  stepIndex: number | null
}

export type ToursSettings = {
  cards: TourProgress
  list: TourProgress
}
type TourKind = keyof ToursSettings

const defaultTourProgress = { completed: false, stepIndex: null } as const

export type Settings = {
  appWasInstalled: boolean
  cardIdsRequiredAutofill: string[]
  cardsFront: CardsFrontUnion
  isInstallPromptDisplayed: boolean
  isPushDailyWordPromptDisplayed: boolean
  learnLanguage: Language
  pushNotificationsEnabled: boolean
  scopeSnoozeToFrontLanguage: boolean
  smartSnoozeEnabled: boolean
  snoozeDelayDays: number
  sourceLanguage: Language
  tours: ToursSettings
  toursDisabled: boolean
  // key is lang code, value is true if voice is available
  voicesAvailability: Record<string, boolean>
  pushSubscriptionEndpoint?: string
}

const normalizeSettingsPatch = (patch: Partial<Settings>) => {
  if (patch.snoozeDelayDays === undefined) return patch

  const parsedDelay = z.int().check(z.minimum(1)).safeParse(patch.snoozeDelayDays)

  return { ...patch, snoozeDelayDays: parsedDelay.success ? parsedDelay.data : config.VITE_DEFAULT_SNOOZE_DELAY_DAYS }
}

const defaultSettings: Settings = {
  appWasInstalled: false,
  cardIdsRequiredAutofill: [],
  cardsFront: 'source',
  isInstallPromptDisplayed: false,
  isPushDailyWordPromptDisplayed: false,
  // main-layout catches empty languages and redirects to onboarding
  // Default values are set this way for convenience to avoid null checks in the consumer code
  learnLanguage: {} as Language,
  sourceLanguage: {} as Language,
  tours: {
    cards: { ...defaultTourProgress },
    list: { ...defaultTourProgress },
  },
  toursDisabled: false,
  voicesAvailability: {},
  pushNotificationsEnabled: false,
  scopeSnoozeToFrontLanguage: true,
  smartSnoozeEnabled: true,
  snoozeDelayDays: config.VITE_DEFAULT_SNOOZE_DELAY_DAYS,
}

const sessionSettingsDefaults: Pick<Settings, 'learnLanguage' | 'sourceLanguage' | 'pushNotificationsEnabled'> & {
  pushSubscriptionEndpoint?: undefined
} = {
  learnLanguage: defaultSettings.learnLanguage,
  sourceLanguage: defaultSettings.sourceLanguage,
  pushNotificationsEnabled: defaultSettings.pushNotificationsEnabled,
  pushSubscriptionEndpoint: undefined,
}

type SettingsActions = {
  advanceTourProgress: (tour: TourKind, stepIndex: number) => void
  clearSessionSettings: () => void
  completeTourProgress: (tour: TourKind) => void
  dequeueCardIdsRequiredAutofill: (cardIds: string[]) => void
  disableTours: () => void
  enqueueCardIdRequiredAutofill: (cardId: string) => void
  getSettings: () => Settings
  patchSettings: (patch: Partial<Settings>) => void
  setLanguagesIfEmpty: (lastSelectedLanguages?: [string, string]) => void
  setVoiceAvailable: (langCode: string, isAvailable: boolean) => void
}

export const useSettingsStore = create<Settings & SettingsActions>()(
  persist(
    (set, get) => ({
      ...defaultSettings,
      advanceTourProgress: (tour, stepIndex) =>
        set(prev => ({
          tours: {
            ...prev.tours,
            [tour]: { completed: false, stepIndex },
          },
        })),
      clearSessionSettings: () => set(state => ({ ...state, ...sessionSettingsDefaults })),
      completeTourProgress: tour =>
        set(prev => ({
          tours: {
            ...prev.tours,
            [tour]: { completed: true, stepIndex: null },
          },
        })),
      dequeueCardIdsRequiredAutofill: cardIds =>
        set(prev => ({
          cardIdsRequiredAutofill: prev.cardIdsRequiredAutofill.filter(id => !cardIds.includes(id)),
        })),
      disableTours: () => set({ toursDisabled: true }),
      enqueueCardIdRequiredAutofill: cardId =>
        set(prev =>
          prev.cardIdsRequiredAutofill.includes(cardId)
            ? prev
            : { cardIdsRequiredAutofill: [...prev.cardIdsRequiredAutofill, cardId] },
        ),
      getSettings: () => get(),
      patchSettings: patch => set(() => normalizeSettingsPatch(patch)),
      setLanguagesIfEmpty: (languages?: [string, string]) => {
        if (!languages) return

        const [source, learn] = languages
        set(prev =>
          prev.sourceLanguage?.value
            ? prev
            : {
                sourceLanguage: { label: languagesJson[source as keyof typeof languagesJson], value: source },
                learnLanguage: { label: languagesJson[learn as keyof typeof languagesJson], value: learn },
              },
        )
      },
      setVoiceAvailable: (langCode: string, isAvailable: boolean) =>
        set(prev => ({ voicesAvailability: { ...prev.voicesAvailability, [langCode]: isAvailable } })),
    }),
    {
      name: 'lingo-cards-settings',
      version: 5,
      migrate: (persistedState, version) => {
        const state = { ...(persistedState as Record<string, unknown>) }

        if (version < 1) {
          if (state.nativeLanguage) {
            state.sourceLanguage = state.nativeLanguage
            delete state.nativeLanguage
          }

          if (state.cardsFront === 'native') state.cardsFront = 'source'
        }

        if (version < 2) state.smartSnoozeEnabled = true

        if (version < 3) state.cardIdsRequiredAutofill = []

        if (version < 4) {
          state.toursDisabled = false
          state.tours = {
            list: { ...defaultTourProgress },
            cards: { ...defaultTourProgress },
          }
        }

        if (version < 5) state.scopeSnoozeToFrontLanguage = true

        return state as Settings
      },
    },
  ),
)
